/**
 * Barra de tabs por contestada do workspace (E8.F5.T06) — visível com K ≥ 2. // ux-neutrality:allow doc-comment
 * Extraída de WorkspaceCasoPage em E36.F4 (max-lines). // ux-neutrality:allow doc-comment
 */
import { ptBR } from '@/i18n/pt-BR';
import type { CaseAnalysisData } from '@/lib/case_workspace';
import type { SignatureRecordData } from '@/lib/case_signatures';

/**
 * Resolve o rótulo da tab para uma contestada. Prioridade:
 * 1. `signature.label` (informado pelo perito).
 * 2. Fallback "Questionada N" baseado em índice (N=1 inicial).
 *
 * E8.F5.T09: garante que análises legadas (sem source_document_id ou label)
 * ainda mostrem algo útil na tab — fallback "Questionada N".
 */
function resolveContestedTabLabel(contested: SignatureRecordData | null, index: number): string {
  if (contested?.label && contested.label.trim().length > 0) {
    return contested.label;
  }
  return `${ptBR.workspaceCaso.contestedTabFallbackLabel} ${index + 1}`;
}

export interface ContestedTabsProps {
  analyses: CaseAnalysisData[];
  signatures: SignatureRecordData[];
  activeContestedId: string | null;
  onSelect: (id: string) => void;
}

export function ContestedTabs({
  analyses,
  signatures,
  activeContestedId,
  onSelect,
}: ContestedTabsProps): JSX.Element | null {
  if (analyses.length < 2) return null;
  return (
    <div
      className="workspace-contested-tabs"
      role="tablist"
      aria-label={ptBR.workspaceCaso.contestedTabsLabel}
      data-testid="contested-tabs"
    >
      {analyses.map((analysis, idx) => {
        const sig = signatures.find((s) => s.id === analysis.contested_signature_id) ?? null;
        const label = resolveContestedTabLabel(sig, idx);
        const selected = activeContestedId === analysis.contested_signature_id;
        return (
          <button
            key={analysis.contested_signature_id}
            type="button"
            role="tab"
            aria-selected={selected}
            className={
              selected
                ? 'workspace-contested-tab workspace-contested-tab--active'
                : 'workspace-contested-tab'
            }
            onClick={() => onSelect(analysis.contested_signature_id)}
            data-testid={`contested-tab-${idx}`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
